import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router";

import {
  type InvocationRecord,
  getInvocationInput,
} from "@/shared/domain/actions";
import { EntityIcon } from "@/shared/foundation/icons";
import { Tooltip } from "@/shared/foundation/primitives";
import { EmptyCell } from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import styles from "./InvocationInputCell.module.css";
import {
  type InputLine,
  type InputScope,
  plural,
  queryClauses,
  summarizeInvocationInput,
} from "./summarizeInvocationInput";

/** Names shown inline before the rest fold into a count. */
const INLINE_NAMES = 2;

/** Icons sit beside xs monospace text, so they're drawn a step under EntityIcon's default. */
const ICON_SIZE = 14;

const QUERY_LABELS: Record<InputScope, string> = {
  session: "Session query",
  file: "File query",
  topic: "Topic query",
};

/** The page a record of this scope has, if any; its id is drawn as a link to it. */
const ID_ROUTES: Record<InputScope, ((id: string) => string) | null> = {
  session: (id) => Goto.createSessionRoute(id),
  file: (id) => Goto.createFileRoute(id),
  topic: null,
};

/**
 * What an invocation ran on, as one line in the invocations list: the lead selector, linked where
 * it names a record with a page, and a "+N more" naming the rest. Old records without
 * `rich_input_data` are read through their data source and paths.
 */
export function InvocationInputCell({
  invocation,
}: {
  invocation: InvocationRecord;
}) {
  const { line, selectors } = summarizeInvocationInput(
    getInvocationInput(invocation, true),
  );
  if (line === null) {
    return <EmptyCell />;
  }
  return (
    <span className={styles.cell}>
      <LineContent line={line} />
      {selectors.length > 1 && (
        <HoverText
          className={styles.muted}
          tooltip={<TooltipLines label="Input" lines={selectors} />}
        >
          +{selectors.length - 1} more
        </HoverText>
      )}
    </span>
  );
}

function LineContent({ line }: { line: InputLine }) {
  switch (line.kind) {
    case "ids": {
      const route = ID_ROUTES[line.scope];
      return (
        <>
          <ScopeIcon scope={line.scope} />
          {route === null ? (
            <span className={styles.mono}>{line.ids[0]}</span>
          ) : (
            <Link
              to={route(line.ids[0])}
              className={classNames(styles.link, styles.mono)}
            >
              {line.ids[0]}
            </Link>
          )}
          <MoreCount rest={line.ids.slice(1)} />
        </>
      );
    }
    case "names":
      return (
        <>
          <ScopeIcon scope={line.scope} />
          <HoverText
            className={classNames(styles.names, styles.mono)}
            underline={false}
            tooltip={<TooltipLines lines={line.names} mono />}
          >
            {line.names.slice(0, INLINE_NAMES).join(", ")}
          </HoverText>
          <MoreCount rest={line.names.slice(INLINE_NAMES)} />
        </>
      );
    case "query":
      return (
        <>
          <ScopeIcon scope={line.scope} />
          <HoverText
            className={classNames(styles.query, styles.mono)}
            tooltip={
              <TooltipLines
                label={QUERY_LABELS[line.scope]}
                lines={queryClauses(line.query)}
                mono
              />
            }
          >
            {line.query}
          </HoverText>
        </>
      );
    case "dataset":
      return (
        <>
          <EntityIcon
            type="dataset"
            size={ICON_SIZE}
            className={styles.icon}
            title
          />
          <Link
            to={Goto.createDatasetRoute(line.datasetId)}
            className={styles.link}
          >
            {line.datasetId}
          </Link>
          {line.pathCount !== null && (
            <span className={styles.muted}>
              {plural(line.pathCount, "path")}
            </span>
          )}
        </>
      );
  }
}

/** The icon of the record type a line selects, named for assistive tech since nothing else says it. */
function ScopeIcon({ scope }: { scope: InputScope }) {
  return (
    <EntityIcon type={scope} size={ICON_SIZE} className={styles.icon} title />
  );
}

/** A "+N" pill for the ids or names a line leaves out, naming them on hover. */
function MoreCount({ rest }: { rest: string[] }) {
  if (rest.length === 0) {
    return null;
  }
  return (
    <HoverText
      className={styles.count}
      ariaLabel={`${rest.length} more: ${rest.join(", ")}`}
      underline={false}
      tooltip={<TooltipLines lines={rest} mono />}
    >
      +{rest.length}
    </HoverText>
  );
}

/**
 * Text with a tooltip. Focusable so keyboard users get the tooltip too; its accessible name is
 * its own text unless `ariaLabel` says more, as a count pill's has to.
 */
function HoverText({
  className,
  tooltip,
  ariaLabel,
  underline = true,
  children,
}: {
  className?: string;
  tooltip: React.ReactNode;
  ariaLabel?: string;
  underline?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        delay={250}
        aria-label={ariaLabel}
        render={
          <span
            tabIndex={0}
            className={classNames(className, {
              [styles.hoverable]: underline,
            })}
          />
        }
      >
        {children}
      </Tooltip.Trigger>
      <Tooltip.Content side="bottom" align="start" className={styles.tooltip}>
        {tooltip}
      </Tooltip.Content>
    </Tooltip>
  );
}

function TooltipLines({
  label,
  lines,
  mono = false,
}: {
  label?: string;
  lines: string[];
  mono?: boolean;
}) {
  return (
    <span className={styles.tooltipBody}>
      {label !== undefined && (
        <span className={styles.tooltipLabel}>{label}</span>
      )}
      {lines.map((line, index) => (
        <span
          key={index}
          className={classNames({ [styles.tooltipLine]: mono })}
        >
          {line}
        </span>
      ))}
    </span>
  );
}
